/**
 * Phase 4 §3 — coach-saved custom formations.
 *
 * localStorage-backed v1 list. Mirrors the style of
 * `tacticsPatternsStore.ts` (same package) — single STORE_KEY, the
 * write path catches and warns rather than throws, the read path
 * returns `[]` on any JSON parse failure.
 *
 * The canonical shape lives in `@4cm/core/tactics/formationsExtended`
 * (TacticsCustomFormation). This store is intentionally thin: it just
 * owns the persistence layer and exposes typed CRUD operations.
 *
 * TODO(Phase 4 v2): swap to a `tactics_custom_formations` Prisma table
 *   once the schema lands. The shape here is the source of truth for
 *   that future SQL migration.
 */

import {
  isTacticsCustomFormation,
  type ExtendedFormationSlot,
  type TacticsCustomFormation,
} from '@4cm/core/tactics/formationsExtended';

const STORE_KEY = '4cm_custom_formations';

/** Input shape for save — id + createdAt are minted by the store when absent. */
export interface SaveCustomFormationInput {
  /** Optional — when present, performs an update; otherwise mints a new id. */
  id?: string;
  name: string;
  slots: ExtendedFormationSlot[];
}

function readRaw(): TacticsCustomFormation[] {
  try {
    const raw =
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isTacticsCustomFormation);
  } catch {
    return [];
  }
}

function writeRaw(list: TacticsCustomFormation[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(list));
  } catch (err) {
    console.warn('[customFormationsStore] could not persist', err);
  }
}

function mintId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * List all saved custom formations, sorted by `createdAt` descending
 * (most recent first). Returns `[]` when storage is empty or corrupt.
 */
export function listCustomFormations(): TacticsCustomFormation[] {
  const list = readRaw();
  list.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  return list;
}

/**
 * Upsert a custom formation. When `id` is present, replaces the row
 * with that id (preserving `createdAt`); otherwise mints a fresh id
 * and stamps `createdAt` to now.
 */
export function saveCustomFormation(
  input: SaveCustomFormationInput,
): TacticsCustomFormation {
  const list = readRaw();
  const now = new Date().toISOString();
  const id = input.id ?? mintId();
  const priorIndex = list.findIndex((f) => f.id === id);
  const prior = priorIndex >= 0 ? list[priorIndex] : null;

  const entry: TacticsCustomFormation = {
    id,
    name: input.name,
    createdAt: prior?.createdAt ?? now,
    slots: input.slots,
  };

  if (priorIndex >= 0) {
    list[priorIndex] = entry;
  } else {
    list.push(entry);
  }
  writeRaw(list);
  return entry;
}

/** Hard-delete a formation. No-op when the id isn't present. */
export function deleteCustomFormation(id: string): void {
  const list = readRaw();
  const next = list.filter((f) => f.id !== id);
  if (next.length === list.length) return;
  writeRaw(next);
}

/**
 * Rename a custom formation in place. Returns the updated row, or
 * `null` when no formation with that id exists.
 */
export function renameCustomFormation(
  id: string,
  name: string,
): TacticsCustomFormation | null {
  const list = readRaw();
  const idx = list.findIndex((f) => f.id === id);
  if (idx < 0) return null;
  const updated: TacticsCustomFormation = { ...list[idx], name };
  list[idx] = updated;
  writeRaw(list);
  return updated;
}
